import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Link,
} from '@mui/material';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNotifications } from '../../hooks/useNotifications';
import { resetPassword } from '../../api/auth';
import { resetPasswordStyles } from './ResetPassword.styles';
import { t } from '../../utils/translations';

const ResetPasswordForm: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { showError, showSuccess } = useNotifications();

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return t('register.passwordMinLength');
    }
    if (!/[a-z]/.test(pwd)) {
      return t('register.passwordLowerCase');
    }
    if (!/[A-Z]/.test(pwd)) {
      return t('register.passwordUpperCase');
    }
    if (!/\d/.test(pwd)) {
      return t('register.passwordDigit');
    }
    if (!/[@$!%*?&]/.test(pwd)) {
      return t('register.passwordSpecialChar');
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password.trim() || !passwordConfirmation.trim()) {
      showError(t('resetPassword.allFieldsRequired'), t('common.error'));
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      showError(passwordError, t('common.error'));
      return;
    }

    if (password !== passwordConfirmation) {
      showError(t('register.passwordMismatch'), t('common.error'));
      return;
    }

    if (!requestId) {
      showError(t('resetPassword.invalidLink'), t('common.error'));
      return;
    }

    if (!executeRecaptcha) {
      showError(t('auth.recaptchaFailed'), t('common.error'));
      return;
    }

    setIsLoading(true);

    try {
      const recaptchaToken = await executeRecaptcha('reset_password');

      await resetPassword(requestId, { password, passwordConfirmation, recaptchaToken });
      showSuccess(t('resetPassword.success'), t('common.success'));
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('resetPassword.failed');
      showError(errorMessage, t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={resetPasswordStyles.container}>
      <Paper elevation={3} sx={resetPasswordStyles.paper}>
        <Box sx={resetPasswordStyles.content}>
          <Typography variant="h4" component="h1" color="primary" sx={resetPasswordStyles.title}>
            Listum
          </Typography>

          <Typography variant="h5" component="h2" sx={resetPasswordStyles.subtitle}>
            {t('resetPassword.title')}
          </Typography>

          <Typography variant="body2" sx={resetPasswordStyles.description}>
            {t('resetPassword.description')}
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={resetPasswordStyles.form}>
            <TextField
              fullWidth
              label={t('resetPassword.newPassword')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              helperText={t('register.passwordHint')}
              sx={resetPasswordStyles.input}
            />

            <TextField
              fullWidth
              label={t('resetPassword.confirmPassword')}
              type="password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              required
              sx={resetPasswordStyles.input}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={resetPasswordStyles.submitButton}
            >
              {isLoading ? <CircularProgress size={24} /> : t('resetPassword.submitButton')}
            </Button>

            <Box sx={resetPasswordStyles.backToLoginLink}>
              <Link href="/" underline="hover">
                {t('resetPassword.backToLogin')}
              </Link>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

const ResetPassword: React.FC = () => {
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY;

  if (!recaptchaSiteKey) {
    return (
      <Container>
        <Typography color="error">
          {t('auth.recaptchaNotConfigured')}
        </Typography>
      </Container>
    );
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      <ResetPasswordForm />
    </GoogleReCaptchaProvider>
  );
};

export default ResetPassword;
